import { ChangeDetectionStrategy, inject, Component, Inject, OnChanges, SimpleChanges, input, signal } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';

import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { Select } from 'primeng/select';
import { InputMask } from 'primeng/inputmask';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { ConsultorioHorarioFeatureService } from './consultorio-horario.service';
import { ConsultorioHorarioCreateRequest, ConsultorioHorarioRequest } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-horario.model';
import { finalize } from 'rxjs/operators';
import { ActionChildComponent } from '../../../../../../../shared/components/containers/action-child.component';
import { BotaoAcoesType } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { MenuItem } from 'primeng/api';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

interface LinhaHorario {
    diaSemana: string;
    manhaInicio?: string | null;
    manhaFim?: string | null;
    tardeInicio?: string | null;
    tardeFim?: string | null;
}

import { CardContent } from '@/shared/components/card-content/card-content';
import { comIdempotencia, novaChaveIdempotencia } from 'src/interceptors/idempotencia-interceptor';

@Component({
    selector: 'app-consultorio-horario',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, CardModule, ButtonModule, TooltipModule, CustomTableComponent, Select, InputMask, CardContent],
    templateUrl: './consultorio-horario.html',
    styleUrls: ['./consultorio-horario.scss']
})
/** Aba de horários de atendimento do consultório privado. */
export class ConsultorioHorarioComponent extends ActionChildComponent implements OnChanges {
    private readonly logger = inject(LoggerService);
    constructor(
        @Inject(ConsultorioHorarioFeatureService)
        private feature: ConsultorioHorarioFeatureService,
        private messageService: MessageService
    ) {
        super();
    }

    protected readonly roleConsultorioEditar = PERMISSOES.medicos.consultorio.editar;

    // Pessoa de contexto
    pessoa = input<Pessoa>();
    // Consultório selecionado
    idConsultorio = input<number | null>(null);

    // Opções de dias da semana
    diasSemanaOptions = [
        { name: 'Domingo', value: 1 },
        { name: 'Segunda-feira', value: 2 },
        { name: 'Terça-feira', value: 3 },
        { name: 'Quarta-feira', value: 4 },
        { name: 'Quinta-feira', value: 5 },
        { name: 'Sexta-feira', value: 6 },
        { name: 'Sábado', value: 7 }
    ];

    // Filtros/campos superiores (conforme imagem)
    diaSemanaInicio: number | null = null;
    diaSemanaFim: number | null = null;

    manhaInicio: string | null = null;
    manhaFim: string | null = null;
    tardeInicio: string | null = null;
    tardeFim: string | null = null;
    isEditing = false;

    /**
     * Chave de idempotência da sessão do formulário de horário. Gira ao entrar em edição, não
     * a cada envio: assim o reenvio após um erro corrigido reusa a mesma chave, e um segundo
     * horário cadastrado em seguida ganha chave nova.
     */
    private chaveIdempotencia = novaChaveIdempotencia();

    // Grid de horários cadastrados
    horarios = signal<LinhaHorario[]>([]);

    // Estados
    loading = signal(false);
    erro = signal<string | undefined>(undefined);

    tableMetadata: TableMetadata = {
        headers: {
            diaSemana: { type: 'text', value: 'Dia da Semana', hidden: false, default: '' },
            manhaInicio: { type: 'text', value: 'Horário Manhã (de)', hidden: false, default: '' },
            manhaFim: { type: 'text', value: 'Horário Manhã (até)', hidden: false, default: '' },
            tardeInicio: { type: 'text', value: 'Horário Tarde (de)', hidden: false, default: '' },
            tardeFim: { type: 'text', value: 'Horário Tarde (até)', hidden: false, default: '' }
        },
        title: 'diaSemana',
        actions: {
            remover: {
                label: '',
                icon: 'pi pi-trash',
                color: 'danger',
                permission: this.roleConsultorioEditar,
                command: (item: unknown) => this.deletar(item)
            }
        }
    };

    actions: BotaoAcoesType & { useEdit: boolean } = {
        items: [
            {
                label: 'Adicionar',
                icon: 'pi pi-plus-circle',
                command: () => this.novo(),
                styleClass: 'bg-green-500',
                permission: this.roleConsultorioEditar
            }
        ],
        useEdit: false
    };

    override initComponent(): void {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa'] || changes['idConsultorio']) {
            const p = this.pessoa();
            const idC = this.idConsultorio();
            if (p && p.id !== undefined && p.id !== null && !!p.uf && typeof idC === 'number') {
                this.carregar({ id: p.id, uf: p.uf, idConsultorio: idC });
            } else {
                this.horarios.set([]);
            }
        }
    }

    private carregar(params: Pessoa & { idConsultorio: number }): void {
        this.loading.set(true);
        this.erro.set(undefined);
        this.feature
            .buscar(params)
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (lista: ConsultorioHorarioRequest[] | null | undefined) => {
                    this.horarios.set(
                        (lista ?? []).map(
                            (r: ConsultorioHorarioRequest): LinhaHorario => ({
                                diaSemana: this.resolverDiaSemana(r),
                                manhaInicio: this.formatarHora(r.dsHoraManhaInicio),
                                manhaFim: this.formatarHora(r.dsHoraManhaFim),
                                tardeInicio: this.formatarHora(r.dsHoraTardeInicio),
                                tardeFim: this.formatarHora(r.dsHoraTardeFim)
                            })
                        )
                    );
                },
                error: (err: unknown) => {
                    this.logger.error('Erro ao buscar horários do consultório', err);
                    this.horarios.set([]);
                    this.erro.set('Falha ao carregar horários do consultório.');
                }
            });
    }

    private resolverDiaSemana(r: ConsultorioHorarioRequest): string {
        const ini = r.diaSemanaInicio?.trim();
        const fim = r.diaSemanaFim?.trim();

        // Prioriza os campos estruturados de início/fim para evitar exibição "X a X".
        if (r.idDiaSemanaInicio === r.idDiaSemanaFim) return ini || fim || '';
        if (ini && fim && ini !== fim) return `${ini} a ${fim}`;
        if (ini || fim) return ini || fim || '';

        const dia = r.dia?.trim();
        if (dia) {
            // Fallback para payload legado que pode vir como "Segunda-feira a Segunda-feira".
            const partes = dia
                .split(/\s+a\s+/i)
                .map((p) => p.trim())
                .filter(Boolean);
            if (partes.length === 2 && partes[0] === partes[1]) return partes[0];
            return dia;
        }

        return ini || fim || '';
    }

    private formatarHora(h?: string | null): string | null {
        if (!h) return '';
        // Mantém como string HH:mm se já vier nesse formato; caso contrário, retorna o valor bruto
        const m = String(h).match(/^\d{2}:\d{2}/);
        return m ? m[0] : String(h);
    }

    override getDefinedActions(): BotaoAcoesType & { useEdit: boolean } {
        return this.actions;
    }

    novo() {
        this.chaveIdempotencia = novaChaveIdempotencia();
        this.isEditing = true;
    }

    cancelarCadastroHorario(): void {
        this.isEditing = false;
        this.resetFormularioHorario();
    }

    salvarHorario(): void {
        const p = this.pessoa();
        const idC = this.idConsultorio();
        if (!p || p.id == null || !p.uf || typeof idC !== 'number') {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Dados do consultório não informados.' });
            return;
        }

        if (!this.diaSemanaInicio || !this.diaSemanaFim) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione os dias da semana de início e fim.' });
            return;
        }

        const hasManha = !!this.manhaInicio || !!this.manhaFim;
        const hasTarde = !!this.tardeInicio || !!this.tardeFim;
        if (!hasManha && !hasTarde) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe ao menos um horário (manhã ou tarde).' });
            return;
        }

        if ((!!this.manhaInicio && !this.manhaFim) || (!this.manhaInicio && !!this.manhaFim)) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe início e fim do período da manhã.' });
            return;
        }

        if ((!!this.tardeInicio && !this.tardeFim) || (!this.tardeInicio && !!this.tardeFim)) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Informe início e fim do período da tarde.' });
            return;
        }

        if (!this.horarioValido(this.manhaInicio) || !this.horarioValido(this.manhaFim) || !this.horarioValido(this.tardeInicio) || !this.horarioValido(this.tardeFim)) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Horário inválido. Use o formato HH:mm.' });
            return;
        }

        const manhaIni = this.horaParaMinutos(this.manhaInicio);
        const manhaFim = this.horaParaMinutos(this.manhaFim);
        const tardeIni = this.horaParaMinutos(this.tardeInicio);
        const tardeFim = this.horaParaMinutos(this.tardeFim);

        // #16590: em cada período, o horário final deve ser maior que o inicial.
        if (manhaIni !== null && manhaFim !== null && manhaFim <= manhaIni) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Horário da manhã: o horário final deve ser maior que o inicial.' });
            return;
        }
        if (tardeIni !== null && tardeFim !== null && tardeFim <= tardeIni) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Horário da tarde: o horário final deve ser maior que o inicial.' });
            return;
        }

        // #16591: a tarde deve começar após o término da manhã (períodos ordenados, sem inverter manhã/tarde).
        if (manhaFim !== null && tardeIni !== null && tardeIni < manhaFim) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'O horário da tarde deve começar após o término da manhã.' });
            return;
        }

        const horario: ConsultorioHorarioCreateRequest = {
            idDiaSemanaInicio: this.diaSemanaInicio,
            idDiaSemanaFim: this.diaSemanaFim,
            dsHoraManhaInicio: this.manhaInicio || null,
            dsHoraManhaFim: this.manhaFim || null,
            dsHoraTardeInicio: this.tardeInicio || null,
            dsHoraTardeFim: this.tardeFim || null
        };

        this.loading.set(true);
        this.feature
            .inserirHorarios(
                { id: p.id, uf: p.uf },
                {
                    idConsultorio: idC,
                    horarios: [horario]
                },
                comIdempotencia(this.chaveIdempotencia)
            )
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Horário inserido com sucesso.' });
                    this.cancelarCadastroHorario();
                    this.carregar({ id: p.id, uf: p.uf, idConsultorio: idC });
                },
                error: (err) => {
                    // Sem toast local; o formulário de horário permanece preenchido para correção.
                    this.logger.error('Erro ao inserir horário do consultório', err);
                }
            });
    }

    private horarioValido(valor: string | null): boolean {
        if (!valor) return true;
        return /^([01]\d|2[0-3]):[0-5]\d$/.test(valor);
    }

    /** Converte 'HH:mm' em minutos desde 00:00; retorna null quando vazio ou fora do formato. */
    private horaParaMinutos(valor: string | null): number | null {
        if (!valor) return null;
        const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(valor);
        return m ? Number(m[1]) * 60 + Number(m[2]) : null;
    }

    private resetFormularioHorario(): void {
        this.diaSemanaInicio = null;
        this.diaSemanaFim = null;
        this.manhaInicio = null;
        this.manhaFim = null;
        this.tardeInicio = null;
        this.tardeFim = null;
    }
    deletar(item: unknown) {
        this.logger.info('DELETAR', item);
    }

    override updateActionsWithIndexes(_actions: [MenuItem, number][]) {}
    override toggleAction(action: MenuItem): MenuItem {
        return action;
    }
}
